import * as Sentry from '@sentry/nextjs';

import { ensureError } from './helpers/ensureError';
import { safeStringify } from './helpers/safeStringify';
import { normalizeUnknownForExtras } from './helpers/normalizeUnknownForExtras';

/**
 * @description
 * Captures a real exception and sends it to Sentry with optional context and extra data.
 *
 * Rules:
 * - Converts any unknown value to an `Error` using `ensureError`.
 * - If `context` is provided, attaches it as a Sentry tag.
 * - If `extraData` is provided, it is attached to the event.
 * - Sends the exception to Sentry.
 *
 * @example
 * ```ts
 * const eventId = captureAppException(new Error('Oops'), 'MyComponent', { userId: 123 });
 * ```
 *
 * @param {unknown} error The unknown error input.
 * @param {string} [context] Optional context label for tracking.
 * @param {Record<string, unknown>} [extraData] Optional extra data to attach to the event.
 * @returns {string} The ID of the captured Sentry event.
 */
export function captureAppException(
  error: unknown,
  context?: string,
  extraData?: Record<string, unknown>
): string {
  try {
    const exception = ensureError(error, context ?? 'Unhandled exception');

    return Sentry.withScope((scope) => {
      if (context) scope.setTag('context', context);

      // Merge provided extras with normalized original (when original was not an Error)
      const normalized = normalizeUnknownForExtras(error);
      if (extraData || normalized) {
        scope.setExtras({
          ...(normalized ? { original: normalized } : {}),
          ...extraData,
        });
      }

      return Sentry.captureException(exception);
    });
  } catch (internalError) {
    // Fallback: attempt to at least capture the original error; if that also fails, swallow.
    try {
      const ensured = ensureError(
        error,
        context ?? 'Unhandled exception (fallback)'
      );
      return Sentry.captureException(ensured);
    } catch {
      // last resort: return empty event id to avoid throwing inside caller code paths
      return '';
    }
  }
}

/**
 * @description
 * Captures an informational or warning message and sends it to Sentry.
 *
 * Rules:
 * - Accepts a message string and an optional error.
 * - If `error` is provided, appends its message (or string form) to the message.
 * - If `extraData` is provided, it is attached to the event.
 * - Tags the scope with "message" context before sending.
 *
 * @example
 * ```ts
 * const eventId = captureAppMessage('Process started', new Error('Minor issue'), { step: 1 });
 * ```
 *
 * @param {string} message The message to log.
 * @param {unknown} [error] Optional error to include in the message.
 * @param {Record<string, unknown>} [extraData] Optional extra data to attach to the event.
 * @returns {string} The ID of the captured Sentry event.
 */
export function captureAppMessage(
  message: string,
  error?: unknown,
  extraData?: Record<string, unknown>
): string {
  try {
    let fullMessage = message;
    if (error) {
      fullMessage +=
        ' | Exception: ' +
        (error instanceof Error ? error.message : safeStringify(error));
    }

    return Sentry.withScope((scope) => {
      scope.setTag('context', 'message');
      const normalized = normalizeUnknownForExtras(error);
      if (extraData || normalized) {
        scope.setExtras({
          ...(normalized ? { original: normalized } : {}),
          ...extraData,
        });
      }
      return Sentry.captureMessage(fullMessage);
    });
  } catch (internalError) {
    // Fallback: minimal capture to avoid losing signal entirely.
    try {
      return Sentry.captureMessage(message);
    } catch {
      return '';
    }
  }
}
